import { AUDIT_BASE_URL } from '../../utils/ApiUrls';

class AdminAuditHistoryService {
    /**
     * Get audit program history
     * @param {string} accessToken - JWT access token
     * @param {number} programId - Audit program ID
     * @param {object} params - Query parameters (search, action_type, ordering, page, page_size)
     */
    async getAuditProgramHistory(accessToken, programId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            if (params.action_type) queryParams.append('action_type', params.action_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/programs/${programId}/history/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit program history'
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred'
            };
        }
    }

    /**
     * Get audit plan history
     * @param {string} accessToken - JWT access token
     * @param {number} planId - Audit plan ID
     * @param {object} params - Query parameters (search, action_type, ordering, page, page_size)
     */
    async getAuditPlanHistory(accessToken, planId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            if (params.action_type) queryParams.append('action_type', params.action_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/plans/${planId}/history/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit plan history'
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred'
            };
        }
    }

    

    /**
     * Get audit section history
     * @param {string} accessToken - JWT access token
     * @param {number} sectionId - Audit section ID
     * @param {object} params - Query parameters (search, action_type, ordering, page, page_size)
     */
    async getAuditSectionHistory(accessToken, sectionId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            if (params.action_type) queryParams.append('action_type', params.action_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/sections/${sectionId}/history/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit section history'
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred'
            };
        }
    }

    /**
     * Get audit question history
     * @param {string} accessToken - JWT access token
     * @param {number} questionId - Audit question ID
     * @param {object} params - Query parameters (search, action_type, ordering, page, page_size)
     */
    async getAuditQuestionHistory(accessToken, questionId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            if (params.action_type) queryParams.append('action_type', params.action_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/questions/${questionId}/history/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit question history'
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred'
            };
        }
    }

    /**
     * Get recommendation action history
     * @param {string} accessToken - JWT access token
     * @param {number} actionId - Recommendation action ID
     * @param {object} params - Query parameters (search, action_type, ordering, page, page_size)
     */
    async getRecommendationActionHistory(accessToken, actionId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.search) queryParams.append('search', params.search);
            if (params.action_type) queryParams.append('action_type', params.action_type);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);

            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/recommendation-actions/${actionId}/history/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch recommendation action history'
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred'
            };
        }
    }

}

const adminAuditHistoryService = new AdminAuditHistoryService();
export default adminAuditHistoryService;
